import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "../img/Logo_signcast_big-1080x322.png";
import useAuthStore from "../store/authStore";

const VerifySignup = () => {
  const [verificationCode, setVerificationCode] = useState("");
  const [countdown, setCountdown] = useState(600); // 10 minutes in seconds
  const { 
    verifySignup, 
    verificationEmail, 
    resendVerificationCode, 
    clearVerification,
    isLoading, 
    error, 
    clearError 
  } = useAuthStore();
  const navigate = useNavigate();
  
  // Redirect if no verification email is found
  useEffect(() => {
    if (!verificationEmail) {
      navigate('/signup');
    }
  }, [verificationEmail, navigate]);
  
  // Setup countdown timer
  useEffect(() => {
    if (!verificationEmail) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [verificationEmail]);
  
  // Format countdown to minutes:seconds
  const formatCountdown = () => {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  const handleVerify = async (e) => {
    e.preventDefault();
    clearError();
    
    // Basic validation
    if (!verificationCode) {
      return;
    }
    
    const success = await verifySignup(verificationCode);
    
    // Navigate to dashboard if verification is successful
    if (success) {
      navigate('/dashboard');
    }
  };
  
  const handleResendCode = async () => {
    clearError();
    const success = await resendVerificationCode();
    
    if (success) {
      // Reset countdown on successful resend
      setCountdown(600);
    }
  };
  
  const handleCancel = () => {
    clearVerification();
    navigate('/signup');
  };
  
  // If no verification is in progress, redirect to signup
  if (!verificationEmail) {
    return null;
  }
  
  return (
    <div className="login-container bg-blue-50 w-full h-screen flex justify-center items-center">
      <div className="bg-white w-[420px] h-[520px] rounded-lg shadow-lg">
        <div className="flex flex-row justify-center pt-10">
          <img className="w-52 h-auto" src={Logo} alt="Logo" />
        </div>
        
        <h1 className="text-2xl font-bold px-10 pt-5">Verify Your Email</h1>
        <p className="text-gray-600 text-sm px-10 py-2">
          We've sent a verification code to <strong>{verificationEmail}</strong>
        </p>
        
        <form onSubmit={handleVerify} className="mt-4">
          {error && (
            <p className="text-red-500 text-[12px] text-center mt-2">{error}</p>
          )}
          
          <div className="flex flex-col px-10">
            <label className="text-black text-[14px] pb-2">
              Enter verification code
            </label>
            <input
              className="text-[20px] pl-4 py-2 w-full h-[45px] rounded-lg border-2 border-gray-300 tracking-wider text-center font-medium"
              type="text"
              placeholder="000000"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
              required
            />
            <div className="flex justify-between items-center mt-3">
              <p className="text-gray-500 text-sm">
                Code expires in: <span className="font-bold">{formatCountdown()}</span>
              </p>
              <button 
                type="button" 
                onClick={handleResendCode}
                disabled={isLoading || countdown > 540} // Disable for first minute
                className={`text-blue-500 text-sm underline ${(isLoading || countdown > 540) ? 'opacity-50 cursor-not-allowed' : 'hover:text-blue-700'}`}
              >
                Resend code
              </button>
            </div>
          </div>
          
          <div className="flex flex-row justify-between px-10 pt-10">
            <button
              type="button"
              onClick={handleCancel}
              className="h-[41px] px-4 bg-gray-200 text-gray-700 text-[14px] font-medium rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={isLoading || verificationCode.length !== 6}
              className={`h-[41px] px-8 bg-blue-500 text-white text-[14px] font-bold rounded-lg hover:bg-blue-600 transition-colors ${(isLoading || verificationCode.length !== 6) ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? "Verifying..." : "Verify & Sign In"}
            </button>
          </div>
        </form>
        
        <p className="text-gray-500 text-sm px-10 mt-8 text-center">
          Didn't receive the code? Check your spam folder or try a different email address.
        </p>
      </div>
    </div>
  );
};

export default VerifySignup; 